import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  ShoppingCart,
  Trash2,
  ArrowRight,
  AlertCircle,
  Loader2,
  PackageOpen,
  Ruler,
  ChevronDown,
} from "lucide-react";
import { API_BASE_URL } from '../../config';
import { LoadingOverlay } from '../LoadingSpinner';
import { getTokenCosts } from '../../services/api';
import './ContentStyles.css';

function CartContent({ onTokenUpdate, onCheckout }) {
  const { t } = useTranslation();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [message, setMessage] = useState("");
  const [tokenCosts, setTokenCosts] = useState({ order_placement: 2 }); // Default to 2 tokens
  const [updatingItems, setUpdatingItems] = useState({}); // Track which items are being updated
  
  // Available embroidery formats from Wilcom
  const EMBROIDERY_FORMATS = [
    // Industrial
    { code: "dst", name: "DST", brand: "Tajima", category: "Industrial" },
    { code: "dsb", name: "DSB", brand: "Barudan", category: "Industrial" },
    { code: "dsz", name: "DSZ", brand: "ZSK", category: "Industrial" },
    { code: "exp", name: "EXP", brand: "Melco", category: "Industrial" },
    { code: "tbf", name: "TBF", brand: "Barudan", category: "Industrial" },
    { code: "fdr", name: "FDR", brand: "Fortron", category: "Industrial" },
    { code: "stx", name: "STX", brand: "Sunstar", category: "Industrial" },
    
    // Domestic
    { code: "pes", name: "PES", brand: "Brother / Babylock", category: "Domestic" },
    { code: "pec", name: "PEC", brand: "Brother", category: "Domestic" },
    { code: "jef", name: "JEF", brand: "Janome / Elna", category: "Domestic" },
    { code: "sew", name: "SEW", brand: "Janome", category: "Domestic" },
    { code: "hus", name: "HUS", brand: "Husqvarna", category: "Domestic" },
    { code: "vip", name: "VIP", brand: "Husqvarna / Viking", category: "Domestic" },
    { code: "vp3", name: "VP3", brand: "Husqvarna / Pfaff", category: "Domestic" },
    { code: "xxx", name: "XXX", brand: "Singer", category: "Domestic" },
    
    // Commercial
    { code: "cmd", name: "CMD", brand: "Compucon", category: "Commercial" },
    { code: "tap", name: "TAP", brand: "Happy", category: "Commercial" },
    { code: "tim", name: "TIM", brand: "Tajima", category: "Commercial" },
    { code: "emt", name: "EMT", brand: "Inbro", category: "Commercial" },
    { code: "10o", name: "10O", brand: "Barudan", category: "Commercial" },
    { code: "ds9", name: "DS9", brand: "Tajima", category: "Commercial" },
  ];

  useEffect(() => {
    loadCart();
    loadTokenBalance();
    loadTokenCosts();
  }, []);

  const loadTokenCosts = async () => {
    try {
      const data = await getTokenCosts();
      if (data && data.costs) {
        setTokenCosts(data.costs);
      }
    } catch (error) {
      console.error("Failed to load token costs:", error);
      // Keep default value on error
    }
  };

  const loadCart = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/cart/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        const cartItems = data.cart_items || [];
        setCartItems(cartItems);
      } else {
        setMessage(`❌ ${data.error || t("cart.failedLoadCart")}`);
      }
    } catch (error) {
      setMessage(t("cart.failedLoadCartRetry"));
    } finally {
      setLoading(false);
    }
  };

  const loadTokenBalance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tokens/balance/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await response.json();
      setTokenBalance(data.tokens || 0);
    } catch (error) {
      console.error("Failed to load token balance:", error);
    }
  };

  const handleRemoveFromCart = async (itemId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/cart/${itemId}/remove/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setCartItems(cartItems.filter((item) => item.id !== itemId));
        setMessage(t("cart.removedSuccess"));
      } else {
        setMessage(`❌ ${data.error || t("cart.failedRemove")}`);
      }
    } catch (error) {
      setMessage(t("cart.failedRemoveRetry"));
    }
  };

  const updateCartItem = useCallback(async (itemId, updates) => {
    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/cart/${itemId}/update/`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (data.success) {
        setCartItems(prev => prev.map(item => 
          item.id === itemId ? data.cart_item : item
        ));
      } else {
        setMessage(`❌ ${data.error || t("cart.failedUpdate")}`);
      }
    } catch (error) {
      setMessage(`❌ ${t("cart.failedUpdateRetry")}`);
    } finally {
      setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  }, [t]);

  // Debounced size update
  const handleSizeChange = useCallback((itemId, newSize) => {
    // Update locally immediately for responsive UI
    setCartItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, embroidery_size_cm: newSize } : item
    ));
    // Debounce the API call
    if (window._sizeUpdateTimers?.[itemId]) {
      clearTimeout(window._sizeUpdateTimers[itemId]);
    }
    if (!window._sizeUpdateTimers) window._sizeUpdateTimers = {};
    window._sizeUpdateTimers[itemId] = setTimeout(() => {
      updateCartItem(itemId, { embroidery_size_cm: newSize });
    }, 400);
  }, [updateCartItem]);

  const handleFormatChange = useCallback((itemId, newFormat) => {
    // Update locally immediately  
    setCartItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, requested_format: newFormat } : item
    ));
    updateCartItem(itemId, { requested_format: newFormat });
  }, [updateCartItem]);

  const handlePlaceOrder = async () => {
    const totalCost = cartItems.reduce((sum, item) => sum + (item.price_in_tokens || tokenCosts.order_placement), 0);

    if (tokenBalance < totalCost) {
      setMessage(t("cart.insufficientTokensMessage"));
      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      // Formats are now per-item (stored on each cart item), send a fallback just in case
      const fallbackFormats = cartItems.map(item => item.requested_format || "pes");
      const uniqueFormats = [...new Set(fallbackFormats)];

      const response = await fetch(`${API_BASE_URL}/cart/checkout/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ requested_formats: uniqueFormats }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage(t("cart.placedSuccess"));
        setCartItems([]);
        setTokenBalance(tokenBalance - totalCost);
        if (onTokenUpdate) onTokenUpdate();
        setTimeout(() => {
          if (onCheckout) onCheckout();
        }, 1500);
      } else {
        setMessage(`❌ ${data.error || t("cart.failedPlaceOrder")}`);
      }
    } catch (error) {
      setMessage(t("cart.failedPlaceOrderRetry"));
    } finally {
      setSubmitting(false);
    }
  };

  const totalItems = cartItems.length;
  const totalCost = cartItems.reduce((sum, item) => sum + (item.price_in_tokens || tokenCosts.order_placement), 0);
  const hasInsufficientTokens = tokenBalance < totalCost;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          background: "#F9FAFB",
        }}
      >
        <Loader2 size={40} color="#3b82f6" className="spin" />
      </div>
    );
  }

  return (
    <>
      <LoadingOverlay visible={loading || submitting} text={submitting ? t("cart.processingOrder") : t("cart.loadingCart")} />
      <div className="cart-wrapper" style={{ padding: "24px", height: "100%", overflowY: "auto", background: "#F9FAFB" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Header */}
          <div style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <ShoppingCart size={28} color="#111827" />
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#111827", margin: 0 }}>
              {t("cart.title")}
            </h1>
          </div>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            {totalItems === 0
              ? t("cart.empty")
              : t("cart.itemCount", { count: totalItems })}
          </p>
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              padding: "12px 16px",
              background: message.includes("❌") ? "#FEE2E2" : "#D1FAE5",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: message.includes("❌") ? "#991B1B" : "#065F46",
              fontSize: "14px",
            }}
          >
            <span>{message}</span>
            <button
              onClick={() => setMessage("")}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
                color: "inherit",
                fontSize: "18px",
              }}
            >
              ×
            </button>
          </div>
        )}

        {totalItems === 0 ? (
          // Empty Cart
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "60px 20px",
              textAlign: "center",
            }}
          >
            <PackageOpen size={64} style={{ margin: "0 auto 20px", color: "#d1d5db" }} />
            <h3 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "12px", color: "#111827" }}>
              {t("cart.empty")}
            </h3>
            <p style={{ color: "#6b7280", marginBottom: "24px", fontSize: "14px" }}>
              {t("cart.addDesignsHint")}
            </p>
            <button
              onClick={() => (window.location.href = "/dashboard")}
              style={{
                padding: "12px 24px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {t("cart.createDesign")}
              <ArrowRight size={18} />
            </button>
          </div>
        ) : (
          <div className="cart-grid-container" style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: "24px" }}>
            {/* Cart Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {cartItems.map((item) => {
                const currentFormat = EMBROIDERY_FORMATS.find(f => f.code === (item.requested_format || item.design_details?.requested_format || 'pes'));
                return (
                <div
                  key={item.id}
                  style={{
                    background: "white",
                    borderRadius: "12px",
                    padding: "20px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    opacity: updatingItems[item.id] ? 0.7 : 1,
                    transition: "opacity 0.2s",
                  }}
                >
                  <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
                  {/* Thumbnail */}
                  <div
                    style={{
                      width: "120px",
                      height: "120px",
                      background: "#f3f4f6",
                      borderRadius: "8px",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {item.design_details?.embroidery_preview || item.design_details?.normal_image ? (
                      <img
                        src={item.design_details.embroidery_preview || item.design_details.normal_image}
                        alt={item.design_details.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#d1d5db",
                        }}
                      >
                        <ShoppingCart size={40} />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1 }}>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        marginBottom: "8px",
                        color: "#111827",
                      }}
                    >
                      {item.design_details?.name || t("cart.untitledDesign")}
                    </h3>
                    {item.design_details?.prompt && (
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#6b7280",
                          marginBottom: "12px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item.design_details.prompt}
                      </p>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                      <div
                        style={{
                          padding: "6px 12px",
                          background: "#f3f4f6",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        {t("cart.quantity")}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Ruler size={14} color="#667eea" />
                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#667eea" }}>
                          {item.embroidery_size_cm || 10} cm
                        </span>
                      </div>
                      <div
                        style={{
                          padding: "4px 10px",
                          background: "#dbeafe",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#1e40af",
                        }}
                      >
                        {currentFormat?.name || (item.requested_format || 'PES').toUpperCase()}
                      </div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#667eea",
                        }}
                      >
                        {item.price_in_tokens || tokenCosts.order_placement} {t("buyTokens.tokens")}
                      </div>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemoveFromCart(item.id)}
                    style={{
                      padding: "10px",
                      background: "#fef2f2",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      color: "#dc2626",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                    title={t("cart.removeFromCart")}
                  >
                    <Trash2 size={20} />
                  </button>
                  </div>

                  {/* Size Slider & Format Selector */}
                  <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #f3f4f6" }}>
                    <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
                      {/* Size Slider */}
                      <div style={{ flex: "1", minWidth: "220px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", display: "flex", alignItems: "center", gap: "6px" }}>
                            <Ruler size={14} />
                            {t("cart.designSize")}
                          </label>
                          <span style={{ 
                            fontSize: "14px", fontWeight: "700", color: "#667eea",
                            background: "#eef2ff", padding: "2px 10px", borderRadius: "12px"
                          }}>
                            {item.embroidery_size_cm || 10} cm
                          </span>
                        </div>
                        <div style={{ position: "relative" }}>
                          <input
                            type="range"
                            min="5"
                            max="40"
                            step="1"
                            value={item.embroidery_size_cm || 10}
                            onChange={(e) => handleSizeChange(item.id, parseInt(e.target.value))}
                            style={{
                              width: "100%",
                              height: "6px",
                              borderRadius: "3px",
                              appearance: "none",
                              WebkitAppearance: "none",
                              background: `linear-gradient(to right, #667eea 0%, #667eea ${((item.embroidery_size_cm || 10) - 5) / 35 * 100}%, #e5e7eb ${((item.embroidery_size_cm || 10) - 5) / 35 * 100}%, #e5e7eb 100%)`,
                              cursor: "pointer",
                              outline: "none",
                            }}
                          />
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                            <span style={{ fontSize: "11px", color: "#9ca3af" }}>5 cm</span>
                            <span style={{ fontSize: "11px", color: "#9ca3af" }}>40 cm</span>
                          </div>
                        </div>
                      </div>

                      {/* Format Selector */}
                      <div style={{ flex: "1", minWidth: "200px" }}>
                        <label style={{ fontSize: "13px", fontWeight: "600", color: "#374151", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                          <ChevronDown size={14} />
                          {t("cart.fileFormat")}
                        </label>
                        <div style={{ position: "relative", marginTop: "8px" }}>
                          <select
                            value={item.requested_format || item.design_details?.requested_format || "pes"}
                            onChange={(e) => handleFormatChange(item.id, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "10px 36px 10px 12px",
                              borderRadius: "8px",
                              border: "1px solid #d1d5db",
                              background: "white",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#111827",
                              cursor: "pointer",
                              appearance: "none",
                              WebkitAppearance: "none",
                              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "right 12px center",
                            }}
                          >
                            {["Industrial", "Domestic", "Commercial"].map(category => (
                              <optgroup key={category} label={category}>
                                {EMBROIDERY_FORMATS.filter(f => f.category === category).map(format => (
                                  <option key={format.code} value={format.code}>
                                    {format.name} – {format.brand}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Cart Summary */}
            <div>
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  position: "sticky",
                  top: "24px",
                }}
              >
                <h2 style={{ fontSize: "20px", fontWeight: "600", marginBottom: "20px", color: "#111827" }}>
                  {t("cart.orderSummary")}
                </h2>

                {/* Per-Item Cost Breakdown */}
                <div style={{ marginBottom: "20px" }}>
                  {cartItems.map((item) => {
                    const format = EMBROIDERY_FORMATS.find(f => f.code === (item.requested_format || 'pes'));
                    return (
                      <div key={item.id} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "8px 0", borderBottom: "1px solid #f3f4f6", fontSize: "13px"
                      }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ color: "#374151", fontWeight: "500" }}>
                            {item.design_details?.name?.substring(0, 20) || t("cart.untitledDesign")}
                          </span>
                          <div style={{ display: "flex", gap: "8px", marginTop: "2px" }}>
                            <span style={{ color: "#9ca3af", fontSize: "11px" }}>
                              {item.embroidery_size_cm || 10} cm
                            </span>
                            <span style={{ color: "#9ca3af", fontSize: "11px" }}>
                              {format?.name || (item.requested_format || 'PES').toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <span style={{ fontWeight: "600", color: "#667eea", whiteSpace: "nowrap" }}>
                          {item.price_in_tokens || tokenCosts.order_placement} {t("buyTokens.tokens")}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "12px",
                      fontSize: "14px",
                    }}
                  >
                    <span style={{ color: "#6b7280" }}>{t("cart.items", { count: totalItems })}</span>
                    <span style={{ fontWeight: "600", color: "#111827" }}>
                      {totalCost} {t("buyTokens.tokens")}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      paddingTop: "12px",
                      borderTop: "1px solid #e5e7eb",
                      fontSize: "16px",
                    }}
                  >
                    <span style={{ fontWeight: "600", color: "#111827" }}>{t("cart.total")}</span>
                    <span style={{ fontWeight: "700", color: "#667eea", fontSize: "18px" }}>
                      {totalCost} {t("buyTokens.tokens")}
                    </span>
                  </div>
                </div>

                {/* Token Balance */}
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#f9fafb",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                    <span style={{ color: "#6b7280" }}>{t("cart.yourBalance")}</span>
                    <span
                      style={{
                        fontWeight: "700",
                        color: hasInsufficientTokens ? "#dc2626" : "#059669",
                      }}
                    >
                      {tokenBalance} {t("buyTokens.tokens")}
                    </span>
                  </div>
                  {hasInsufficientTokens && (
                    <div
                      style={{
                        marginTop: "8px",
                        padding: "8px 12px",
                        background: "#fee2e2",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        color: "#991b1b",
                        fontSize: "13px",
                      }}
                    >
                      <AlertCircle size={16} />
                      <span>{t("cart.insufficientTokens")}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <button
                  onClick={handlePlaceOrder}
                  disabled={submitting || hasInsufficientTokens}
                  style={{
                    width: "100%",
                    padding: "14px",
                    background:
                      submitting || hasInsufficientTokens
                        ? "#d1d5db"
                        : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: submitting || hasInsufficientTokens ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    marginBottom: "12px",
                  }}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="spin" />
                      {t("cart.placingOrder")}
                    </>
                  ) : (
                    <>
                      {t("cart.placeOrder")}
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                {hasInsufficientTokens && (
                  <button
                    onClick={() => (window.location.href = "/dashboard/buy-tokens")}
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      marginBottom: "12px",
                    }}
                  >
                    {t("cart.buyMoreTokens")}
                  </button>
                )}

                <button
                  onClick={() => (window.location.href = "/dashboard")}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: "white",
                    color: "#374151",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  {t("cart.continueShopping")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .spin {
            animation: spin 1s linear infinite;
          }
          /* Range slider thumb styling */
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: #667eea;
            cursor: pointer;
            border: 3px solid white;
            box-shadow: 0 1px 4px rgba(0,0,0,0.2);
            margin-top: -7px;
          }
          input[type="range"]::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #667eea;
            cursor: pointer;
            border: 3px solid white;
            box-shadow: 0 1px 4px rgba(0,0,0,0.2);
          }
          input[type="range"]::-webkit-slider-runnable-track {
            height: 6px;
            border-radius: 3px;
          }
        `}
      </style>
    </div>
    </>
  );
}

export default CartContent;
